"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePortalContainer } from "../../../contexts/index.js";
import { useIsBelowMdViewport } from "../../../hooks/useShouldUseDrawer.js";
import { mergeRefs } from "../../../utils/mergeRefs.js";
import { InputButton } from "../../InputButton/index.js";
import { OverlaySearchHeader, OverlayTitle } from "../../OverlaySearchHeader/index.js";
import { Popover } from "../../Popover/index.js";
import { Calendar } from "../Calendar/Calendar.js";
import { CustomMonth } from "../CustomComponents/CustomMonth.js";
import { Footer } from "../CustomComponents/Footer.js";
import { getDefaultMonth, getFormattedDates, shouldAutoClose } from "../helpers/helpers.js";
const DEFAULT_DATE_PICKER_FORMAT = 'ccc, MMM d';
const MOBILE_NUMBER_OF_MONTHS = 12;
function createDatePickerFooterComponent(props) {
    return function() {
        return /*#__PURE__*/ jsx(Footer, {
            ...props
        });
    };
}
function DatePickerWrapper({ mode = 'single', handleCalendarChange, footerLeftNode, subHeading, components: initialComponents, label, selected, initialSelected, displayNights, isOpen: isCalendarOpen = false, customDateFormat, defaultMonth: defaultMonthProp, renderTrigger, onOpenChange, numberOfMonths, heading = 'Choose Dates', popOverText, collisionAvoidance, placeholder, isDisabled, errorMessage, helperText, errorText, inputRef, ...restProps }) {
    const [isOpen, setIsOpen] = useState(isCalendarOpen);
    const showDrawerOverlay = useIsBelowMdViewport();
    const portalContainer = usePortalContainer();
    const setOpen = useCallback((open)=>{
        setIsOpen(open);
        onOpenChange?.(open);
    }, [
        onOpenChange
    ]);
    const [calendarState, setCalendarState] = useState({
        selectedDates: initialSelected ?? selected,
        defaultMonth: getDefaultMonth({
            mode,
            selected: initialSelected ?? selected
        }) ?? defaultMonthProp
    });
    const formattedDates = useMemo(()=>calendarState.selectedDates ? getFormattedDates({
            dates: calendarState.selectedDates,
            dateFormat: customDateFormat ?? DEFAULT_DATE_PICKER_FORMAT,
            displayNights
        }) : '', [
        calendarState.selectedDates,
        customDateFormat,
        displayNights
    ]);
    const selectedKey = useMemo(()=>{
        if (!selected) return '';
        if (selected instanceof Date) return selected.getTime().toString();
        if ('from' in selected) return `${selected.from?.getTime() ?? ''}-${selected.to?.getTime() ?? ''}`;
        if (Array.isArray(selected)) return selected.map((d)=>d.getTime()).join(',');
        return '';
    }, [
        selected
    ]);
    const prevSelectedKeyRef = useRef(selectedKey);
    useEffect(()=>{
        if (initialSelected || isOpen) return;
        if (selectedKey === prevSelectedKeyRef.current) return;
        prevSelectedKeyRef.current = selectedKey;
        setCalendarState({
            selectedDates: selected,
            defaultMonth: getDefaultMonth({
                mode,
                selected
            }) ?? defaultMonthProp
        });
    }, [
        defaultMonthProp,
        initialSelected,
        isOpen,
        mode,
        selected,
        selectedKey
    ]);
    const handleCalendarDates = useCallback((newDates, triggerDate)=>{
        setCalendarState((prev)=>({
                ...prev,
                selectedDates: newDates,
                defaultMonth: getDefaultMonth({
                    mode,
                    selected: newDates
                }) ?? defaultMonthProp
            }));
        handleCalendarChange(newDates, triggerDate);
        if (shouldAutoClose(mode, newDates)) setOpen(false);
    }, [
        mode,
        defaultMonthProp,
        handleCalendarChange,
        setOpen
    ]);
    const value = useMemo(()=>subHeading || formattedDates || '', [
        subHeading,
        formattedDates
    ]);
    const subHeadingValue = useMemo(()=>subHeading || formattedDates || label || '', [
        subHeading,
        formattedDates,
        label
    ]);
    const InputTrigger = useCallback(({ onClick, ref, ...props })=>{
        if (renderTrigger) return renderTrigger({
            ...props,
            onClick,
            ref,
            isOpen
        });
        return /*#__PURE__*/ jsx(InputButton, {
            ...props,
            ref: mergeRefs(ref, inputRef),
            "data-testid": "date-picker-input",
            className: "w-full",
            label: label,
            value: value,
            iconLeft: 'calendar_month',
            size: "lg",
            placeholder: placeholder,
            isDisabled: isDisabled,
            errorMessage: errorMessage,
            helperText: helperText,
            errorText: errorText,
            dataFocusWithin: isOpen,
            onClick: onClick
        });
    }, [
        errorMessage,
        errorText,
        helperText,
        isDisabled,
        inputRef,
        isOpen,
        label,
        placeholder,
        renderTrigger,
        value
    ]);
    const desktopComponents = useMemo(()=>({
            ...initialComponents,
            Month: CustomMonth,
            Footer: createDatePickerFooterComponent({
                footerLeftNode,
                handleFooterCallBack: setOpen
            })
        }), [
        initialComponents,
        footerLeftNode,
        setOpen
    ]);
    const mobileComponents = useMemo(()=>({
            ...initialComponents,
            Month: CustomMonth
        }), [
        initialComponents
    ]);
    const { selectedDates, defaultMonth } = calendarState;
    const calendarProps = {
        ...restProps,
        mode,
        handleCalendarChange: handleCalendarDates,
        selected: selectedDates,
        defaultMonth
    };
    const handleClose = useCallback(()=>setOpen(false), [
        setOpen
    ]);
    return /*#__PURE__*/ jsxs(Popover.Root, {
        mode: showDrawerOverlay ? 'drawer' : 'popover',
        open: isOpen,
        onOpenChange: setOpen,
        snapPoints: [
            1
        ],
        disablePointerDismissal: true,
        children: [
            /*#__PURE__*/ jsx(Popover.Trigger, {
                render: InputTrigger,
                openOnHover: false
            }),
            /*#__PURE__*/ jsxs(Popover.Portal, {
                container: showDrawerOverlay ? void 0 : portalContainer || void 0,
                children: [
                    showDrawerOverlay && /*#__PURE__*/ jsx(Popover.Backdrop, {}),
                    /*#__PURE__*/ jsx(Popover.Positioner, {
                        sideOffset: 8,
                        align: "start",
                        collisionAvoidance: collisionAvoidance,
                        positionMethod: "fixed",
                        children: showDrawerOverlay ? /*#__PURE__*/ jsxs(Popover.Popup, {
                            drawerHeight: "full",
                            children: [
                                /*#__PURE__*/ jsx(Popover.Title, {
                                    className: "sr-only",
                                    children: "Calendar"
                                }),
                                /*#__PURE__*/ jsx(Popover.Description, {
                                    className: "sr-only",
                                    children: "Overlay Calendar"
                                }),
                                /*#__PURE__*/ jsx(OverlaySearchHeader, {
                                    children: /*#__PURE__*/ jsx(OverlayTitle, {
                                        heading: heading,
                                        subHeading: subHeadingValue,
                                        handleClose: handleClose,
                                        popOverText: popOverText
                                    })
                                }),
                                /*#__PURE__*/ jsx("div", {
                                    className: "relative flex w-full flex-1 overflow-y-auto p-4",
                                    children: /*#__PURE__*/ jsx(Calendar, {
                                        ...calendarProps,
                                        numberOfMonths: MOBILE_NUMBER_OF_MONTHS,
                                        isMobile: true,
                                        autoFocus: true,
                                        scrollToSelectedMonthOnOpen: isOpen,
                                        components: mobileComponents
                                    })
                                })
                            ]
                        }) : /*#__PURE__*/ jsxs(Popover.Popup, {
                            size: "auto",
                            className: "p-4",
                            children: [
                                /*#__PURE__*/ jsx(Popover.Arrow, {}),
                                /*#__PURE__*/ jsx(Popover.Title, {
                                    className: "sr-only",
                                    children: label
                                }),
                                /*#__PURE__*/ jsx(Calendar, {
                                    ...calendarProps,
                                    numberOfMonths: numberOfMonths,
                                    components: desktopComponents
                                })
                            ]
                        })
                    })
                ]
            })
        ]
    });
}
const DatePicker = /*#__PURE__*/ memo(DatePickerWrapper);
export { DEFAULT_DATE_PICKER_FORMAT, DatePicker };
